import { selectPlaceShowDifferentSizeOnMap } from 'containers/MapDetails/placeSlice'
import { selectPlaygroundPlaceId } from 'containers/Playground/playgroundSlice'
import { addNotification } from 'containers/Notifications/notificationsSlice'
import usePlaceFilters from 'containers/PlaceMap/hooks/usePlaceFilters'
import useObjectHover from 'containers/Playground/hooks/useObjectHover'
import { selectUserId } from 'containers/Login/loginSlice'
import { useReduxDispatch, useReduxSelector } from 'store'
import { useHistory, useParams } from 'react-router-dom'
import type { SelectedObjects } from './WarehouseList'
import useTranslation from 'hooks/useTranslation'
import WarehouseNotes from './WarehouseNotes'
import React from 'react'

interface WarehouseProps extends playground.Warehouse {
  isChildren?: boolean
  selectedObjects: SelectedObjects
}

const Warehouse: React.FC<WarehouseProps> = ({ isChildren = false, selectedObjects, ...mag }) => {
  const { id, blink, children, name, color_state, params: magParams, rotation, x, y, width, height, style, typeId, warehouseData: wd } = mag

  const [filters, setFilters] = usePlaceFilters()
  const clientId = useReduxSelector(selectUserId)
  const placeId = useReduxSelector(selectPlaygroundPlaceId)
  const showDifferentSize = useReduxSelector(selectPlaceShowDifferentSizeOnMap)
  const params = useParams<{ id: string }>()

  const minDim = Math.min(width, height)
  const isSelected = params.id === typeId
  const isDifferentSize = color_state !== 'view_for_rent' && !!wd?.status?.isAvailableForRent
  const magMainRef = React.useRef<HTMLDivElement>(null)
  const smallWarehouse = (mag?.params?.rotatedDoor ? width < 40 : height < 40)

  const dispatch = useReduxDispatch()
  const { t } = useTranslation('placeMap')
  const { replace, push, goBack } = useHistory()
  const { onHover } = useObjectHover(typeId)


  const displayAlert = (message: string, customAction?: basic.BasicFunc) => {
    dispatch(addNotification({ message, type: 'danger', customAction }))
  }

  const textStyle: React.CSSProperties | undefined = rotation ? { rotate: `${-rotation}deg` } : undefined

  return (
    <div
      id={`object_${id}`}
      className='object'
      style={{
        left: x,
        top: y,
      }}>
      <div
        ref={magMainRef}
        data-type-id={children ? undefined : typeId}
        className={`object__main${isSelected ? ' --selected' : color_state === 'view_for_rent' ? ' --available' : (isDifferentSize && showDifferentSize) ? ' --differentSize' : ' --unavailable'}${blink ? ' --blink' : ''}`}
        onPointerEnter={() => {
          onHover(magMainRef)
        }}
        onClick={() => {
          if (!children) {
            if (!wd?.status?.isAvailableForRent
              || !!wd?.status?.inTemporaryReservation
              || color_state?.includes('muted')) {
              const label = color_state?.includes('purple')
                ? 'youAreAlreadyRentingThisWarehouse'
                : wd?.status?.inTemporaryReservation
                  ? 'warehouseIsCurrentlyBeingRented'
                  : (!wd?.status?.isAvailableForRent || (isDifferentSize && !showDifferentSize))
                    ? 'warehouseIsUnavailable'
                    : 'warehouseDoesNotMeetTheSelectedFilters'
              const action = label === 'warehouseDoesNotMeetTheSelectedFilters' && mag.warehouseData?.attributes?.warehouseTypeId
                ? () => {
                  const { hash, ...attributes } = filters?.attributes || {}
                  setFilters({ ...filters, attributes: { ...attributes, warehouseTypeId: mag.warehouseData?.attributes?.warehouseTypeId } })
                } : undefined
              displayAlert(t(label), action)
              return
            }

            if (isSelected) {
              goBack()
            } else {
              const action = params.id ? replace : push
              action(`/placemap/${placeId}/${typeId}`)
            }
          }
        }}
        style={(() => {
          const base: React.CSSProperties = {
            width: `${width}px`,
            height: `${height}px`,
            rotate: `${rotation}deg`,
            flexDirection: mag.params?.rotatedDoor ? 'row' : 'column',
            borderRadius: `${Math.min(5, Math.max(2, minDim * 0.1))}px`
          }

          if (!isSelected && selectedObjects[typeId]) {
            base.backgroundColor = 'rgba(135, 132, 227, 0.25)'
            base.borderColor = 'rgba(135, 132, 227, 1)'
          }

          const { opacity, backgroundColor, borderColor, color, ...rest } = style
          return { ...rest, ...base }
        })()}>
        {children ? children.map((e, i) => <Warehouse
          {...e}
          key={i}
          isChildren
          selectedObjects={selectedObjects} />
        ) : <>
          <div
            className='object__name'
            style={textStyle}>
            {name}
          </div>
          {/* {note === '5 minut' && <WarehouseTimer
            endTime={Date.now() + (5 * 60 * 1000)}
            style={textStyle}
          />} */}
          {((mag.params?.rotatedDoor ? width : height) > 80 && (
            (wd?.status?.isAvailableForRent || clientId === wd?.renting?.clientId) ||
            (clientId && wd?.renting?.clientId ? clientId === wd?.renting?.clientId : true)
          )) && <WarehouseNotes
              {...mag}
              style={textStyle} />}
          {wd?.rentableType !== 'parking' && !smallWarehouse
            ? <div className={`object__entrance${magParams?.rotatedDoor ? ' --rotated' : ''}`}>
              <div className='entrance__door' style={(() => {
                let base: React.CSSProperties | undefined = undefined
                if (magParams?.rotatedDoor) {
                  base = {
                    ...(base || {}),
                    height: `${height / 2}px`
                  }
                }
                return base
              })()} />
            </div>
            : smallWarehouse
              ? null
              : <div />}
        </>}
      </div>
    </div>
  )
}

export default Warehouse